import { Stack } from '@inflearn/ds-react';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import type { ReactElement } from 'react';
import { MyCoursesLayout } from '../../../components/layout/MyPageLayout/MyCoursesLayout';
import { CourseCardContainer } from '../../../components/pages/my/courses/CourseCardContainer';
import { MyCoursesEmptyStates } from '../../../components/pages/my/courses/MyCoursesEmptyStates';
import { MyCoursesPageFilter } from '../../../components/pages/my/courses/MyCoursesPageFilter';
import { useMyCoursesSearchEvent } from '../../../components/pages/my/courses/hooks/useMyCoursesSearchEvent';
import useUrlStateForMyCourses from '../../../components/pages/my/courses/hooks/useUrlStateForMyCourses';
import useViewMyCoursesPageEvent from '../../../components/pages/my/courses/hooks/useViewMyCoursesPageEvent';
import { useClientApiVoucherMyQuery } from '../../../components/pages/my/courses/queries/useClientApiVoucherMyQuery';

export const getStaticProps = async ({ locale }: { locale: string }) => ({
  props: {
    ...(await serverSideTranslations(locale, ['common'])),
  },
  revalidate: 60,
});

function MyCoursesPage() {
  const { currentFilter, ...pageParam } = useUrlStateForMyCourses();
  const {
    data: myVoucher,
    isLoading,
    isError,
  } = useClientApiVoucherMyQuery({
    query: {
      ...currentFilter,
      pageSize: '42',
    },
  });

  useViewMyCoursesPageEvent({
    myVoucher,
    courseType: currentFilter.courseType,
  });
  useMyCoursesSearchEvent({
    filterValue: currentFilter,
    myVoucher,
    isSearching: isLoading,
  });

  return (
    <Stack spacing="lg">
      <MyCoursesPageFilter filterState={currentFilter} changeFilter={pageParam.setValue} />
      <CourseCardContainer
        isLoading={isLoading}
        isError={isError}
        data={myVoucher}
        courseType={currentFilter.courseType}
        emptyState={
          currentFilter.searchKeyword ? (
            <MyCoursesEmptyStates.NoSearchResultState
              onReset={() =>
                pageParam.setValue({
                  ...currentFilter,
                  searchKeyword: '',
                })
              }
            />
          ) : (
            <MyCoursesEmptyStates.EmptyVoucherState />
          )
        }
        errorState={<MyCoursesEmptyStates.ErrorState />}
        changePage={(value) => pageParam.setValue({ ...currentFilter, pageNumber: `${value}` })}
      />
    </Stack>
  );
}

function MyCoursesPageLayout({ children, pathname }: { children: ReactElement; pathname: string }) {
  const { t } = useTranslation();
  return (
    <MyCoursesLayout
      pathname={pathname}
      title={t('메타.내학습.제목')}
      description={t('메타.내학습.설명')}
    >
      {children}
    </MyCoursesLayout>
  );
}

MyCoursesPage.getLayout = (page: ReactElement, pathname: string) => (
  <MyCoursesPageLayout pathname={pathname}>{page}</MyCoursesPageLayout>
);

export default MyCoursesPage;
